import { useEffect, useMemo, useRef } from "react";
import type { Path, UseFormReturn } from "react-hook-form";

import { type AnyShippingMethod, useFeatureFlags } from "@jti/commerce";
import type { ShippingFormFields } from "@jti/commerce-types";
import type { MagentoShippingMethod } from "@jti/magento-types";

import { useCartSubscriptionStatus } from "@checkout-v2/shared/hooks/useCartSubscriptionStatus";
import { isDeliveryOptionSelectable, prepareDeliveryOptions } from "@checkout-v2/shared/utils/prepareDeliveryOptions";

type UsePreparedDeliveryOptionsArgs = {
  form: UseFormReturn<ShippingFormFields>;
  rawOptions: MagentoShippingMethod[];
  methodType: AnyShippingMethod["type"] | undefined;
  selectedMethodCode: string;
  initialSelectedDeliveryOption?: string;
  fieldName: Path<ShippingFormFields>;
  onChange?: (value: string) => void;
};

const moveSelectedOptionFirst = (
  options: MagentoShippingMethod[],
  selectedMethodCode?: string
): MagentoShippingMethod[] => {
  if (!selectedMethodCode) {
    return options;
  }

  const selectedOptionIndex = options.findIndex((option) => option.methodCode === selectedMethodCode);

  if (selectedOptionIndex <= 0) {
    return options;
  }

  return [
    options[selectedOptionIndex],
    ...options.slice(0, selectedOptionIndex),
    ...options.slice(selectedOptionIndex + 1),
  ];
};

export const usePreparedDeliveryOptions = ({
  form,
  rawOptions,
  methodType,
  selectedMethodCode,
  initialSelectedDeliveryOption,
  fieldName,
  onChange,
}: UsePreparedDeliveryOptionsArgs) => {
  const isSubscription = useCartSubscriptionStatus();
  const initialSelectedDeliveryOptionRef = useRef(initialSelectedDeliveryOption);

  const { ffCheckoutV2EnableIteration2 } = useFeatureFlags();

  const preparedDeliveryOptions = useMemo<MagentoShippingMethod[]>(() => {
    if (methodType === "atHome") {
      if (ffCheckoutV2EnableIteration2) {
        return moveSelectedOptionFirst(
          prepareDeliveryOptions(rawOptions, isSubscription ?? false),
          initialSelectedDeliveryOptionRef.current
        );
      } else {
        return prepareDeliveryOptions(rawOptions, isSubscription ?? false);
      }
    }

    const matchedOption = rawOptions.find((option) => option.methodCode === selectedMethodCode);

    return matchedOption ? [matchedOption] : [];
  }, [rawOptions, selectedMethodCode, methodType, isSubscription, ffCheckoutV2EnableIteration2]);

  useEffect(() => {
    if (isSubscription === undefined) {
      return;
    }

    const currentValue = form.getValues(fieldName);
    const availableOptions = preparedDeliveryOptions.filter((option) =>
      isDeliveryOptionSelectable(option, isSubscription)
    );

    const isCurrentValueValid = currentValue && availableOptions.some((option) => option.methodCode === currentValue);

    let nextValue = currentValue;

    if (currentValue && !isCurrentValueValid) {
      nextValue = "";
    }

    if (!nextValue && availableOptions.length === 1) {
      nextValue = availableOptions[0].methodCode;
    }

    if (nextValue !== currentValue) {
      form.setValue(fieldName, nextValue);
      if (nextValue) {
        onChange?.(nextValue);
      }
    }
  }, [preparedDeliveryOptions, form, fieldName, onChange, isSubscription]);

  return {
    isSubscription,
    preparedDeliveryOptions,
  };
};
